import { useGSAP } from '@gsap/react';
import { gsap } from 'gsap';
import { ScrambleTextPlugin } from 'gsap/ScrambleTextPlugin';
import { SplitText } from 'gsap/SplitText';

/**
 * Las animaciones de la interfaz, con GSAP (gratis para uso comercial; su única restricción es no usarlo en un
 * editor visual de animaciones, que IGOR no es). Todas pasan por `conMovimiento`: si la persona pidió al sistema
 * reducir el movimiento, no se anima nada y todo aparece en su sitio.
 */
gsap.registerPlugin(useGSAP, SplitText, ScrambleTextPlugin);
// Las pantallas animan lo que haya: si una marca aún no tiene miniatura o cuadros, no es un error.
gsap.config({ nullTargetWarn: false });
// Tiempo real siempre: si el equipo va lento, la animación salta en vez de alargarse. Con el suavizado por
// defecto, en una página larga y un equipo modesto el contenido tardaba segundos en aparecer.
gsap.ticker.lagSmoothing(0);

export { gsap, useGSAP };

// Para depurar desde la consola del navegador en local.
if (import.meta.env.DEV) (window as unknown as { gsap: typeof gsap }).gsap = gsap;

const SIN_MOVIMIENTO = '(prefers-reduced-motion: reduce)';

/** Ejecuta `fn` solo si la persona no pidió reducir el movimiento. Devuelve la limpieza para `useGSAP`. */
export function conMovimiento(fn: () => void): () => void {
  const mm = gsap.matchMedia();
  mm.add(`not all and ${SIN_MOVIMIENTO}`, fn);
  return () => mm.revert();
}

/**
 * Las cajas entran de abajo, una detrás de otra. El escalonado total se topa en medio segundo: en una página larga
 * (la estrategia tiene decenas de cajas) un retraso fijo por caja dejaba las últimas en blanco casi tres segundos.
 */
export function entrada(objetivos: gsap.TweenTarget, opts: { retraso?: number; cada?: number } = {}) {
  const cuantas = gsap.utils.toArray(objetivos).length;
  const cada = Math.min(opts.cada ?? 0.07, 0.5 / Math.max(cuantas, 1));
  gsap.from(objetivos, {
    y: 22, opacity: 0, scale: 0.985, duration: 0.6, ease: 'power3.out',
    stagger: cada, delay: opts.retraso ?? 0, clearProps: 'transform,opacity',
  });
}

/** Los números suben desde cero hasta su valor, como un contador. Lee el valor de `data-cuenta`. */
export function contar(raiz: HTMLElement | null, retraso = 0.2) {
  raiz?.querySelectorAll<HTMLElement>('[data-cuenta]').forEach((el) => {
    const fin = Number(el.dataset.cuenta);
    if (!Number.isFinite(fin) || fin <= 0) return;
    const obj = { v: 0 };
    gsap.to(obj, {
      v: fin, duration: 1.1, delay: retraso, ease: 'power2.out',
      onUpdate: () => { el.textContent = String(Math.round(obj.v)); },
    });
  });
}

/** Una caja que se levanta un poco al pasar el ratón por encima. */
export const levantar = (el: Element, arriba: boolean) =>
  gsap.to(el, { y: arriba ? -4 : 0, duration: 0.35, ease: 'power2.out', overwrite: 'auto' });

/**
 * Un texto que aparece por partes. `lineas` y `palabras` suben desde detrás de una máscara, como si salieran de
 * una ranura; `letras` caen una a una con un pequeño giro. SplitText deja el texto entero para los lectores de
 * pantalla y vuelve a partirlo solo si cambia el ancho o terminan de cargar las letras.
 */
export function revelar(
  objetivos: gsap.DOMTarget,
  opts: { por?: 'lineas' | 'palabras' | 'letras'; retraso?: number; cada?: number } = {},
) {
  const por = opts.por ?? 'lineas';
  const tipo = por === 'lineas' ? 'lines' : por === 'palabras' ? 'words' : 'chars';
  return SplitText.create(objetivos, {
    type: tipo === 'chars' ? 'words,chars' : tipo,
    ...(tipo === 'chars' ? {} : { mask: tipo }),
    autoSplit: true,
    onSplit: (self) =>
      tipo === 'chars'
        ? gsap.from(self.chars, {
            yPercent: -60, rotate: -14, opacity: 0, duration: 0.7, ease: 'back.out(2)',
            stagger: opts.cada ?? 0.035, delay: opts.retraso ?? 0,
          })
        : gsap.from(tipo === 'lines' ? self.lines : self.words, {
            yPercent: 110, duration: 0.75, ease: 'power4.out', stagger: opts.cada ?? (tipo === 'lines' ? 0.1 : 0.05),
            delay: opts.retraso ?? 0,
          }),
  });
}

/** El texto cambia revolviendo las letras hasta formar el nuevo, como un tablero de aeropuerto. */
export function revolver(el: Element | null, texto: string) {
  if (!el) return;
  gsap.to(el, { duration: Math.min(1.2, 0.35 + texto.length * 0.018), ease: 'none', scrambleText: { text: texto, chars: 'abcdefghijklmnñopqrstuvwxyz', speed: 0.7, revealDelay: 0.15 } });
}
